import React, { useEffect, useState } from 'react';
import { adminDashboardService } from '../services';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { FaProjectDiagram, FaHandHoldingHeart, FaUsers, FaImages, FaCalendarAlt, FaBullhorn } from 'react-icons/fa';

const Dashboard = () => {
    const [stats, setStats] = useState(null);
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const [statsRes, activitiesRes] = await Promise.all([
                adminDashboardService.getStats(),
                adminDashboardService.getActivities(),
            ]);
            setStats(statsRes.data);
            setActivities(activitiesRes.data);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="text-center py-12"><div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;
    }

    const overviewData = [
        { name: 'Projects', value: stats?.total_projects || 0 },
        { name: 'Events', value: stats?.total_events || 0 },
        { name: 'Donors', value: stats?.total_donors || 0 },
    ];
    const donationStatusData = [
        { name: 'Completed', value: stats?.donation_status?.completed || 0, color: '#10b981' },
        { name: 'Pending', value: stats?.donation_status?.pending || 0, color: '#f59e0b' },
        { name: 'Failed', value: stats?.donation_status?.failed || 0, color: '#ef4444' },
        { name: 'Refunded', value: stats?.donation_status?.refunded || 0, color: '#6b7280' },
    ];
    const monthlyTrendData = (stats?.monthly_donations || []).map((item) => ({
        month: item.month,
        total: Number(item.total || 0),
    }));

    return (
        <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-8">Dashboard</h1>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-8">
                <div className="bg-white p-6 rounded-lg shadow-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Total Projects</p>
                            <p className="text-3xl font-bold text-emerald-600">{stats?.total_projects || 0}</p>
                        </div>
                        <FaProjectDiagram className="text-4xl text-emerald-200" />
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Total Donations</p>
                            <p className="text-3xl font-bold text-emerald-600">PKR {(stats?.total_donations || 0).toLocaleString()}</p>
                        </div>
                        <FaHandHoldingHeart className="text-4xl text-emerald-200" />
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Total Donors</p>
                            <p className="text-3xl font-bold text-emerald-600">{stats?.total_donors || 0}</p>
                        </div>
                        <FaUsers className="text-4xl text-emerald-200" />
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Gallery Images</p>
                            <p className="text-3xl font-bold text-emerald-600">{stats?.total_gallery_images || 0}</p>
                        </div>
                        <FaImages className="text-4xl text-emerald-200" />
                    </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Upcoming Events</p>
                            <p className="text-3xl font-bold text-emerald-600">{stats?.upcoming_events || 0}</p>
                        </div>
                        <FaCalendarAlt className="text-4xl text-emerald-200" />
                    </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Active Campaigns</p>
                            <p className="text-3xl font-bold text-emerald-600">{stats?.active_campaigns || 0}</p>
                        </div>
                        <FaBullhorn className="text-4xl text-emerald-200" />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 mb-8">
                <div className="bg-white p-6 rounded-lg shadow-lg xl:col-span-2">
                    <h2 className="text-xl font-bold text-gray-800 mb-6">Overview</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={overviewData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" fill="#10b981" radius={[6, 6, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                    <h2 className="text-xl font-bold text-gray-800 mb-6">Donation Status</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie data={donationStatusData} dataKey="value" nameKey="name" outerRadius={100} label>
                                {donationStatusData.map((entry) => (
                                    <Cell key={entry.name} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg mb-8">
                <h2 className="text-xl font-bold text-gray-800 mb-6">Donation Trend (Last 6 Months)</h2>
                <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={monthlyTrendData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="total" stroke="#10b981" strokeWidth={3} dot={{ r: 4 }} />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* Recent Activities */}
            <div className="bg-white p-6 rounded-lg shadow-lg">
                <h2 className="text-xl font-bold text-gray-800 mb-6">Recent Activities</h2>
                {activities.recent_projects?.length > 0 && (
                    <div className="mb-6">
                        <h3 className="font-semibold text-gray-700 mb-3">Recent Projects</h3>
                        <div className="space-y-2">
                            {activities.recent_projects.slice(0, 3).map((project) => (
                                <div key={project.id} className="p-3 bg-gray-50 rounded">
                                    <p className="font-semibold text-gray-800">{project.title}</p>
                                    <p className="text-sm text-gray-600">{new Date(project.created_at).toLocaleDateString()}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
